import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  MY_FRIENDS, FRIEND_REQUESTS, SEARCH_USERS, SEND_FRIEND_REQUEST, RESPOND_FRIEND_REQUEST, REMOVE_FRIEND,
  MY_STUDY_GROUPS, CREATE_STUDY_GROUP, ASSIGN_CHAPTER, UPDATE_CHAPTER_STATUS, UPDATE_HOBBIES
} from '../graphql';
import { Users, UserPlus, Search, X, BookOpen, Plus, Check, ChevronRight, Star, Paperclip, Inbox } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUS_MAP = { PENDING:'In Progress', IN_PROGRESS:'In Progress', DONE:'Done' };

function UserCard({ user: u, onAdd, status }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
      <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">{u.name?.[0]?.toUpperCase()}</div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm truncate">{u.name}</p>
        <p className="text-xs text-gray-400 truncate">{u.college} {u.branch ? `· ${u.branch}` : ''}</p>
        {u.skills?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {u.skills.slice(0, 3).map(s => <span key={s} className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-full">{s}</span>)}
            {u.skills.length > 3 && <span className="text-xs text-gray-400">+{u.skills.length-3}</span>}
          </div>
        )}
        {u.hobbies?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {u.hobbies.slice(0, 3).map(h => <span key={h} className="text-xs bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded-full">{h}</span>)}
          </div>
        )}
      </div>
      <div className="flex-shrink-0">
        {status === 'friend' && <span className="text-xs text-green-600 font-semibold">✓ Friend</span>}
        {status === 'pending' && <span className="text-xs text-amber-600">Pending</span>}
        {!status && <button onClick={() => onAdd(u.id)} className="btn-secondary text-xs px-2 py-1 flex items-center gap-1"><UserPlus size={11}/> Add</button>}
      </div>
    </div>
  );
}

function FriendCard({ friendship, onAccept, onDecline, onRemove, isRequest }) {
  const u = friendship.friend;
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
      <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">{u?.name?.[0]?.toUpperCase()}</div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm truncate">{u?.name}</p>
        <p className="text-xs text-gray-400">{u?.college}</p>
        {u?.skills?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {u.skills.slice(0,4).map(s=><span key={s} className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-full">{s}</span>)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {isRequest ? (
          <>
            <button onClick={() => onAccept(friendship.id)} className="btn-primary text-xs px-2 py-1">Accept</button>
            <button onClick={() => onDecline(friendship.id)} className="btn-secondary text-xs px-2 py-1 text-red-500">Decline</button>
          </>
        ) : (
          <button onClick={() => onRemove(friendship.id)} className="text-xs text-gray-300 hover:text-red-400 transition-colors"><X size={14}/></button>
        )}
      </div>
    </div>
  );
}

function StudyGroupCard({ group, currentUserId }) {
  const [expanded, setExpanded] = useState(false);
  const [assignForm, setAssignForm] = useState({ userId: '', chapter: '', notes: '' });
  const [assign] = useMutation(ASSIGN_CHAPTER, { refetchQueries: [MY_STUDY_GROUPS] });
  const [updateStatus] = useMutation(UPDATE_CHAPTER_STATUS, { refetchQueries: [MY_STUDY_GROUPS] });

  const myMember = group.members?.find(m => m.user?.id === currentUserId);
  const isCreator = group.creator?.id === currentUserId;

  return (
    <div className="card !p-0 overflow-hidden">
      <button className="w-full p-4 text-left flex items-center justify-between" onClick={() => setExpanded(e=>!e)}>
        <div>
          <h3 className="font-bold text-gray-900">{group.name}</h3>
          <p className="text-xs text-gray-400">{group.subject} · {group.memberCount} member{group.memberCount!==1?'s':''}</p>
        </div>
        <ChevronRight size={16} className={`text-gray-400 transition-transform ${expanded?'rotate-90':''}`} />
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-3">
          {/* Chapter progress table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="text-left py-1">Member</th>
                  <th className="text-left py-1">Chapter</th>
                  <th className="text-left py-1">Status</th>
                  {myMember && <th className="text-left py-1">Action</th>}
                </tr>
              </thead>
              <tbody>
                {group.members?.map(m => (
                  <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">{m.user?.name?.[0]}</div>
                        <span className="font-medium text-gray-700 text-xs">{m.user?.name}</span>
                      </div>
                    </td>
                    <td className="py-2">
                      {m.chapterAssigned
                        ? <span className="text-xs text-gray-700">{m.chapterAssigned} {m.notesUrl && <a href={m.notesUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline ml-1"><Paperclip size={12} className="inline" /></a>}</span>
                        : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="py-2 text-xs">{m.chapterAssigned ? STATUS_MAP[m.chapterStatus] || m.chapterStatus : '—'}</td>
                    {myMember && m.user?.id === currentUserId && m.chapterAssigned && (
                      <td className="py-2">
                        <select className="text-xs border border-gray-200 rounded px-1 py-0.5" value={m.chapterStatus} onChange={e => updateStatus({ variables: { groupId: group.id, status: e.target.value } })}>
                          {['PENDING','IN_PROGRESS','DONE'].map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Assign chapter (creator only) */}
          {isCreator && (
            <div className="bg-indigo-50 rounded-xl p-3">
              <p className="text-xs font-semibold text-indigo-700 mb-2">Assign Chapter</p>
              <div className="flex gap-2 flex-wrap">
                <select className="border border-gray-200 rounded-lg px-2 py-1 text-xs flex-1 min-w-0" value={assignForm.userId} onChange={e=>setAssignForm(f=>({...f,userId:e.target.value}))}>
                  <option value="">Select member</option>
                  {group.members?.map(m => <option key={m.user?.id} value={m.user?.id}>{m.user?.name}</option>)}
                </select>
                <input className="border border-gray-200 rounded-lg px-2 py-1 text-xs flex-1 min-w-0" placeholder="Chapter (e.g. Unit 3 — Trees)" value={assignForm.chapter} onChange={e=>setAssignForm(f=>({...f,chapter:e.target.value}))} />
                <input className="border border-gray-200 rounded-lg px-2 py-1 text-xs flex-1 min-w-0" placeholder="Notes URL (optional)" value={assignForm.notes} onChange={e=>setAssignForm(f=>({...f,notes:e.target.value}))} />
                <button onClick={() => {
                  if (assignForm.userId && assignForm.chapter) {
                    assign({ variables: { groupId: group.id, userId: assignForm.userId, chapterAssigned: assignForm.chapter, notesUrl: assignForm.notes || null } });
                    setAssignForm({ userId:'',chapter:'',notes:'' });
                  }
                }} className="btn-primary text-xs px-2 py-1">Assign</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function NetworkPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('friends');
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupForm, setGroupForm] = useState({ name:'', subject:'', description:'' });
  const [showHobbies, setShowHobbies] = useState(false);
  const [hobbiesInput, setHobbiesInput] = useState('');
  const [hobbiesArr, setHobbiesArr] = useState([]);
  const [pendingIds, setPendingIds] = useState(new Set());

  const { data: friendsData, refetch: refetchFriends } = useQuery(MY_FRIENDS);
  const { data: reqData, refetch: refetchReqs } = useQuery(FRIEND_REQUESTS);
  const { data: groupsData, refetch: refetchGroups } = useQuery(MY_STUDY_GROUPS);
  const [sendReq] = useMutation(SEND_FRIEND_REQUEST, {
    onCompleted: (_, { variables }) => {
      setPendingIds(s => new Set([...s, variables.userId]));
      refetchFriends();
    },
  });
  const [respond] = useMutation(RESPOND_FRIEND_REQUEST, { onCompleted: () => { refetchFriends(); refetchReqs(); } });
  const [removeFriend] = useMutation(REMOVE_FRIEND, { onCompleted: () => refetchFriends() });
  const [createGroup, { loading: creatingGroup }] = useMutation(CREATE_STUDY_GROUP, { onCompleted: () => { refetchGroups(); setShowCreateGroup(false); }});
  const [updateHobbies] = useMutation(UPDATE_HOBBIES);

  const { refetch: doSearch } = useQuery(SEARCH_USERS, { variables: { query: ' ' }, skip: !searchQ });

  const friends = friendsData?.myFriends || [];
  const requests = reqData?.friendRequests || [];
  const groups = groupsData?.myStudyGroups || [];

  const handleSearch = async () => {
    if (!searchQ.trim()) return;
    const { data } = await doSearch({ query: searchQ });
    setSearchResults(data?.searchUsers || []);
  };

  const friendIds = new Set(friends.map(f => f.friend?.id));
  const pendingFriendIds = new Set([...pendingIds, ...friends.filter(f => f.status === 'PENDING').map(f => f.friend?.id)]);

  const saveHobbies = async () => {
    await updateHobbies({ variables: { hobbies: hobbiesArr } });
    setShowHobbies(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-xl"><Users size={20} className="text-blue-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Network</h1>
            <p className="text-gray-500 text-sm">Connect by skills · Study groups · Divide & conquer chapters</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowHobbies(true)} className="btn-secondary text-sm">My Hobbies</button>
          <button onClick={() => setShowCreateGroup(true)} className="btn-primary text-sm flex items-center gap-1"><Plus size={14}/> Study Group</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-5 w-fit">
        {[['friends',`Friends (${friends.length})`],['requests',`Requests (${requests.length})`],['search','Find People'],['groups','Study Groups']].map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab===t?'bg-white shadow text-gray-900':'text-gray-500'}`}>{l}</button>
        ))}
      </div>

      {/* Friends tab */}
      {tab === 'friends' && (
        <div>
          {friends.length === 0
            ? <div className="text-center py-12"><div className="mb-3"><Users size={40} className="mx-auto text-gray-300" /></div><p className="text-gray-500">No friends yet — search for people to connect.</p><button onClick={() => setTab('search')} className="btn-primary mt-3 text-sm">Find People</button></div>
            : <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {friends.map(f => <FriendCard key={f.id} friendship={f} isRequest={false} onRemove={id => removeFriend({ variables: { friendshipId: id } })} />)}
              </div>
          }
        </div>
      )}

      {/* Requests tab */}
      {tab === 'requests' && (
        <div>
          {requests.length === 0
            ? <div className="text-center py-12"><div className="mb-3"><Inbox size={40} className="mx-auto text-gray-300" /></div><p className="text-gray-500">No pending requests</p></div>
            : <div className="space-y-2">
                {requests.map(f => (
                  <FriendCard key={f.id} friendship={f} isRequest
                    onAccept={id => respond({ variables: { friendshipId: id, accept: true } })}
                    onDecline={id => respond({ variables: { friendshipId: id, accept: false } })} />
                ))}
              </div>
          }
        </div>
      )}

      {/* Search tab */}
      {tab === 'search' && (
        <div>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input className="input pl-8 w-full" placeholder="Search by name, college, or skill…" value={searchQ} onChange={e => setSearchQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} />
            </div>
            <button onClick={handleSearch} className="btn-primary text-sm">Search</button>
          </div>
          {searchResults.length > 0 && (
            <div className="space-y-2">
              {searchResults.map(u => (
                <UserCard key={u.id} user={u}
                  status={friendIds.has(u.id) ? 'friend' : pendingFriendIds.has(u.id) ? 'pending' : undefined}
                  onAdd={uid => sendReq({ variables: { userId: uid } })} />
              ))}
            </div>
          )}
          {searchQ && searchResults.length === 0 && <div className="text-center py-8 text-gray-400">No users found for "{searchQ}"</div>}
        </div>
      )}

      {/* Study Groups tab */}
      {tab === 'groups' && (
        <div className="space-y-3">
          {groups.length === 0 && <div className="text-center py-12"><div className="mb-3"><BookOpen size={40} className="mx-auto text-gray-300" /></div><p className="text-gray-500">No study groups yet</p><button onClick={() => setShowCreateGroup(true)} className="btn-primary mt-3 text-sm">+ Create Group</button></div>}
          {groups.map(g => <StudyGroupCard key={g.id} group={g} currentUserId={user?.id} />)}
        </div>
      )}

      {/* Create Study Group modal */}
      {showCreateGroup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Create Study Group</h2>
              <button onClick={() => setShowCreateGroup(false)}><X size={18} className="text-gray-400"/></button>
            </div>
            <div className="p-5 space-y-3">
              <input className="input w-full" placeholder="Group name (e.g. DBMS Warriors)" value={groupForm.name} onChange={e=>setGroupForm(f=>({...f,name:e.target.value}))} />
              <input className="input w-full" placeholder="Subject (e.g. Database Management)" value={groupForm.subject} onChange={e=>setGroupForm(f=>({...f,subject:e.target.value}))} />
              <textarea className="input w-full resize-none h-16 text-sm" placeholder="Description / goal" value={groupForm.description} onChange={e=>setGroupForm(f=>({...f,description:e.target.value}))} />
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-xs text-blue-700 font-medium mb-1">Divide & Conquer strategy</p>
                <p className="text-xs text-blue-600">After creating the group, add friends from your network then assign each person a chapter/unit. Everyone summarises their part and shares notes — covering the whole syllabus together.</p>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowCreateGroup(false)} className="btn-secondary flex-1">Cancel</button>
                <button onClick={async () => {
                  if (!groupForm.name || !groupForm.subject) return;
                  await createGroup({ variables: { name: groupForm.name, subject: groupForm.subject, description: groupForm.description || null } });
                  setGroupForm({ name:'',subject:'',description:'' });
                }} disabled={creatingGroup || !groupForm.name || !groupForm.subject} className="btn-primary flex-1">
                  {creatingGroup ? 'Creating…' : 'Create Group'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hobbies modal */}
      {showHobbies && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">My Hobbies & Interests</h2>
              <button onClick={() => setShowHobbies(false)}><X size={18} className="text-gray-400"/></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">Your hobbies help match you with sports events and people with similar interests</p>
              <div className="flex gap-2">
                <input className="input flex-1" placeholder="Add hobby (e.g. Cricket, Chess, Coding)" value={hobbiesInput} onChange={e=>setHobbiesInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&hobbiesInput.trim()){setHobbiesArr(h=>[...new Set([...h,hobbiesInput.trim()])]);setHobbiesInput('');}}} />
                <button onClick={() => { if(hobbiesInput.trim()){setHobbiesArr(h=>[...new Set([...h,hobbiesInput.trim()])]);setHobbiesInput('');} }} className="btn-secondary text-sm">Add</button>
              </div>
              {[['Sports','Cricket','Football','Basketball','Badminton','Chess'],['Creative','Music','Photography','Art','Writing'],['Tech','Coding','Gaming','3D Printing','Robotics'],['Others','Yoga','Dance','Cooking','Reading']].map(([cat,...items]) => (
                <div key={cat}>
                  <p className="text-xs text-gray-400 mb-1">{cat}</p>
                  <div className="flex flex-wrap gap-1">
                    {items.map(h => (
                      <button key={h} onClick={() => setHobbiesArr(a => a.includes(h) ? a.filter(x=>x!==h) : [...a,h])} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${hobbiesArr.includes(h) ? 'bg-orange-500 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-orange-300'}`}>{h}</button>
                    ))}
                  </div>
                </div>
              ))}
              {hobbiesArr.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
                  {hobbiesArr.map(h => <span key={h} onClick={() => setHobbiesArr(a=>a.filter(x=>x!==h))} className="text-xs bg-orange-50 text-orange-600 px-2.5 py-1 rounded-full cursor-pointer hover:bg-red-50 hover:text-red-500">{h} ×</span>)}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowHobbies(false)} className="btn-secondary flex-1">Cancel</button>
                <button onClick={saveHobbies} className="btn-primary flex-1">Save Hobbies</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
